import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  onError?: (error: Error) => void;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors from child components.
 * Renders nothing in-place (children disappear).
 * Reports the error up via `onError` so the page can show it at the top.
 */
export class FantasyPointsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[FantasyPointsErrorBoundary] Caught error:", error, info);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export function FantasyErrorAlert({ errors }: { errors: Error[] }) {
  if (errors.length === 0) return null;

  const isDev = import.meta.env.DEV;

  return (
    <div className="space-y-2">
      {errors.map((error, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: error list is stable within a render
        <div
          key={i}
          className="rounded-lg border border-destructive/50 bg-destructive/10 p-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-destructive">
                {error.message}
              </p>
              {isDev && error.stack && (
                <pre className="mt-2 text-xs text-destructive/80 whitespace-pre-wrap break-all font-mono bg-destructive/5 rounded p-2 max-h-48 overflow-y-auto">
                  {error.stack}
                </pre>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
